import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import {
  Toll as TierIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { adminTierConfigApi, AdminBoardTierConfig } from '../../api/tierConfig';
import { useDocumentTitle } from '../../hooks/core';

export function TierConfigPage() {
  useDocumentTitle('Board Tier Pricing');

  const [configs, setConfigs] = useState<AdminBoardTierConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState<AdminBoardTierConfig | null>(null);
  const [editCreditCost, setEditCreditCost] = useState<number>(1);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await adminTierConfigApi.getTierConfigs();
      setConfigs(response.configs || []);
      setError(null);
    } catch (err) {
      setError('Failed to load tier configurations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditOpen = (config: AdminBoardTierConfig) => {
    setEditConfig(config);
    setEditCreditCost(config.creditCost);
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditConfig(null);
    setEditCreditCost(1);
    setEditError(null);
  };

  const handleUpdateConfig = async () => {
    if (!editConfig) return;
    try {
      setEditLoading(true);
      setEditError(null);
      await adminTierConfigApi.updateTierCost(editConfig.numberMode, editCreditCost);
      setSuccess(`${editConfig.displayName} tier updated to ${editCreditCost} credits`);
      handleEditClose();
      fetchData();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update tier';
      setEditError(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const getTierColor = (numberMode: string): 'action' | 'primary' | 'secondary' => {
    switch (numberMode) {
      case 'half':
        return 'primary';
      case 'quarter':
        return 'secondary';
      default:
        return 'action';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Board Tier Pricing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure credit costs for different board number set options
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tier</TableCell>
                <TableCell>Number Mode</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Credit Cost</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((config) => (
                <TableRow key={config.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TierIcon color={getTierColor(config.numberMode)} />
                      <Typography fontWeight="medium">{config.displayName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                      {config.numberMode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {config.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="h6"
                      color={config.numberMode === 'quarter' ? 'secondary.main' : config.numberMode === 'half' ? 'primary.main' : 'text.primary'}
                      fontWeight="bold"
                    >
                      {config.creditCost}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {config.canEdit ? (
                      <Tooltip title="Edit credit cost">
                        <IconButton size="small" onClick={() => handleEditOpen(config)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Only super admins can edit tier pricing">
                        <span>
                          <IconButton size="small" disabled>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          How Tier Pricing Works
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Users are charged the configured number of credits when creating a board based on the number set option they select:
        </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 2 }}>
          <li>
            <Typography variant="body2" color="text.secondary">
              <strong>Same for All Periods (single):</strong> Standard boards use the same random numbers throughout the game
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">
              <strong>Different per Half (half):</strong> Premium boards with two different number sets (1H and 2H)
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">
              <strong>Different per Quarter (quarter):</strong> Premium boards with unique numbers for each scoring period
            </Typography>
          </li>
        </Box>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          Edit Credit Cost
          {editConfig && (
            <Typography variant="body2" color="text.secondary">
              {editConfig.displayName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Credit Cost"
            type="number"
            fullWidth
            value={editCreditCost}
            onChange={(e) => setEditCreditCost(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1, max: 100 }}
            helperText="Minimum 1 credit required"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateConfig}
            variant="contained"
            disabled={editLoading || editCreditCost < 1}
          >
            {editLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TierConfigPage;
